import type { AlbumSaveResult, CapturedFile, LibraryPickOptions, MediaAdapter } from './mediaContract';

// Browser / WKWebView file-input lanes.
//   camera lane  = <input type="file" accept="image/*" capture="environment">  → source 'camera'
//   library lane = <input type="file" accept="image/*" multiple>               → source 'photoLibrary'
//                  (without `multiple` on a single-photo lane: report cover, inspector photo)
// Provenance is decided by which input the app opened (fail-closed), never by the bytes.
// In a browser the library lane is this file input; in the app it is the system photo picker since the
// Owner's ruling of 2026-10-28 (iosMediaAdapter.ts), which falls back to this lane if the picker fails.
//
// There is no system album in a browser: the camera lane reports 'unavailable' — visible on
// the photo, never silent. On the iOS app the native adapter takes over (iosMediaAdapter.ts).
const CANCEL_GRACE_MS = 900;

function openFileInput(options: { capture: boolean; multiple: boolean }): Promise<File[]> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    if (options.capture) input.setAttribute('capture', 'environment');
    input.multiple = options.multiple;
    input.style.position = 'fixed';
    input.style.left = '-9999px';
    input.setAttribute('data-v2-media-lane', options.capture ? 'camera' : 'library');
    document.body.appendChild(input);

    let settled = false;
    let focusTimer: ReturnType<typeof setTimeout> | null = null;
    const finish = (files: File[]) => {
      if (settled) return;
      settled = true;
      if (focusTimer) clearTimeout(focusTimer);
      window.removeEventListener('focus', onFocus);
      input.remove();
      resolve(files);
    };
    const onFocus = () => {
      // The picker closed. Give a late `change` event a moment, then treat it as cancelled.
      focusTimer = setTimeout(() => finish(Array.from(input.files ?? [])), CANCEL_GRACE_MS);
    };
    input.addEventListener('change', () => finish(Array.from(input.files ?? [])));
    input.addEventListener('cancel', () => finish([]));
    window.addEventListener('focus', onFocus);
    input.click();
  });
}

function toCaptured(file: File, source: CapturedFile['source']): CapturedFile {
  return {
    blob: file,
    mimeType: file.type && file.type.length > 0 ? file.type : 'image/jpeg',
    fileName: file.name && file.name.length > 0 ? file.name : `photo-${Date.now()}.jpg`,
    source
  };
}

export const webMediaAdapter: MediaAdapter = {
  platform: 'web',
  async captureFromCamera(): Promise<CapturedFile | null> {
    const files = await openFileInput({ capture: true, multiple: false });
    return files.length > 0 ? toCaptured(files[0], 'camera') : null;
  },
  async pickFromLibrary(options: LibraryPickOptions = {}): Promise<CapturedFile[]> {
    const single = options.single === true;
    const files = await openFileInput({ capture: false, multiple: !single });
    // A host that ignores `multiple` still hands back one photo on a single-photo lane.
    return (single ? files.slice(0, 1) : files).map((f) => toCaptured(f, 'photoLibrary'));
  },
  async saveToSystemAlbum(): Promise<AlbumSaveResult> {
    return {
      state: 'unavailable',
      // Recorded on the photo, never shown: the capture hook tells the inspector in their language
      // (photo.webNoAlbum) — on a computer the original stays where it was picked from.
      detail: 'web: no system album'
    };
  }
};
